import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Eye, EyeOff, AtSign, Lock, KeyRound, ShieldCheck, Users, ChevronRight } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import PWAInstallPrompt from '@/components/PWAInstallPrompt';
import { OldAppBanner } from '@/components/OldAppBanner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const apiErrorText = (error, fallback) => {
  const d = error?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map(e => e?.msg).filter(Boolean).join(' ') || fallback;
  return fallback;
};

const cacheEnseigne = async (token, enseigne) => {
  localStorage.removeItem('store_plan_cache');
  localStorage.removeItem('enseigne_logo_data');
  window.__enseigneFetched = false;
  try {
    let ens = enseigne;
    if (!ens) {
      const r = await axios.get(`${API}/v2/my-enseigne`, { headers: { Authorization: `Bearer ${token}` } });
      ens = r.data?.enseigne;
    }
    if (ens) {
      localStorage.setItem('enseigne_cache', JSON.stringify({
        ...ens, logo_url: ens.logo_url && !ens.logo_url.startsWith('http') ? `${BACKEND_URL}${ens.logo_url}` : ens.logo_url,
        primary_color: ens.primary_color || ens.color || '#0a1b3d',
      }));
      if (ens.slug) localStorage.setItem('enseigne_slug', ens.slug);
      window.dispatchEvent(new Event('enseigne-cache-updated'));
    }
  } catch {}
  window.__enseigneFetched = true;
};

const PasswordSetupStep = ({ token, onDone, color }) => {
  const [pwd, setPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 6) return toast.error('6 caractères minimum');
    if (pwd !== confirm) return toast.error('Les deux mots de passe ne correspondent pas');
    setBusy(true);
    try {
      await axios.post(`${API}/auth/set-password`, { new_password: pwd }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Mot de passe créé ! Votre PIN reste valable pour pointer.');
      onDone();
    } catch (err) { toast.error(apiErrorText(err, 'Impossible de créer le mot de passe')); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="password-setup-step">
      <div className="flex items-start gap-3 rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
        <ShieldCheck className="w-5 h-5 shrink-0 mt-0.5" />
        <p>Vous vous êtes connecté avec votre code PIN. Créez un mot de passe personnel pour sécuriser votre compte — le PIN reste utilisable pour le pointage.</p>
      </div>
      <div className="relative">
        <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input type={show ? 'text' : 'password'} placeholder="Nouveau mot de passe (6+ caractères)" value={pwd} onChange={e => setPwd(e.target.value)} className="h-12 pl-10 pr-12 rounded-xl bg-gray-50" data-testid="setup-password-input" />
        <button type="button" onClick={() => setShow(!show)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400" data-testid="setup-toggle-password">{show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
      </div>
      <Input type={show ? 'text' : 'password'} placeholder="Confirmer le mot de passe" value={confirm} onChange={e => setConfirm(e.target.value)} className="h-12 rounded-xl bg-gray-50" data-testid="setup-confirm-input" />
      <button type="submit" disabled={busy} className="w-full rounded-xl text-white font-bold uppercase tracking-wider py-3.5 disabled:opacity-60" style={{ background: `linear-gradient(180deg, ${color} 0%, #0a1b3d 100%)` }} data-testid="setup-password-submit">
        {busy ? 'Enregistrement...' : 'Créer mon mot de passe'}
      </button>
      <button type="button" onClick={onDone} className="w-full text-sm text-gray-500 hover:text-gray-700 py-1" data-testid="setup-password-skip">Plus tard, continuer avec mon PIN</button>
    </form>
  );
};

const AccountChoiceStep = ({ choice, onPicked, onCancel, color }) => {
  const [busy, setBusy] = useState(null);
  const [left, setLeft] = useState(choice.expires_in || 120);
  useEffect(() => {
    const t = setInterval(() => setLeft(l => (l <= 1 ? 0 : l - 1)), 1000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => { if (left === 0) { toast.error('Délai dépassé, reconnectez-vous.'); onCancel(); } }, [left, onCancel]);
  const pick = async (acc) => {
    setBusy(acc.id);
    try {
      const { data } = await axios.post(`${API}/auth/unified-login/choose`, { choice_token: choice.choice_token, user_id: acc.id });
      onPicked(data);
    } catch (err) {
      toast.error(apiErrorText(err, 'Connexion impossible'));
      if (err.response?.status === 401) onCancel();
    } finally { setBusy(null); }
  };
  return (
    <div className="space-y-4" data-testid="account-choice-step">
      <div className="flex items-start gap-3 rounded-xl bg-blue-50 border border-blue-200 p-3 text-sm text-blue-900">
        <Users className="w-5 h-5 shrink-0 mt-0.5" />
        <p>Plusieurs comptes utilisent cet identifiant. Choisissez celui avec lequel vous souhaitez vous connecter.</p>
      </div>
      <div className="space-y-2">
        {choice.accounts.map(acc => (
          <button key={acc.id} type="button" disabled={!!busy} onClick={() => pick(acc)}
            className="w-full flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-gray-50 hover:bg-white hover:border-[#4578A6] hover:shadow-md px-4 py-3 text-left transition-all active:scale-[0.99] disabled:opacity-60"
            data-testid={`account-choice-${acc.id}`}>
            <div className="min-w-0">
              <p className="font-semibold text-gray-900 truncate">{acc.first_name} {acc.last_name}</p>
              <p className="text-xs text-gray-500 truncate">{acc.role_label}{acc.store_name ? ` · ${acc.store_name}` : ''}</p>
            </div>
            {busy === acc.id ? <span className="animate-spin w-5 h-5 border-2 border-[#4578A6] border-t-transparent rounded-full shrink-0" /> : <ChevronRight className="w-5 h-5 text-gray-400 shrink-0" />}
          </button>
        ))}
      </div>
      <div className="flex items-center justify-between text-xs text-gray-400">
        <button type="button" onClick={onCancel} className="hover:text-gray-600" data-testid="account-choice-cancel">Retour</button>
        <span data-testid="account-choice-timer">Choix valable {Math.floor(left / 60)}:{String(left % 60).padStart(2, '0')}</span>
      </div>
    </div>
  );
};

const Login = () => {
  const [identifier, setIdentifier] = useState('');
  const [secret, setSecret] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [loading, setLoading] = useState(false);
  const [enseigne, setEnseigne] = useState(null);
  const [ready, setReady] = useState(false);
  const [setup, setSetup] = useState(null);
  const [choice, setChoice] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { enseigneSlug } = useParams();
  const { user, loading: authLoading, login } = useAuth();
  const color = enseigne?.primary_color || '#4578A6';

  useEffect(() => {
    if (!enseigneSlug) { setReady(true); return; }
    axios.get(`${API}/v2/enseigne-by-slug/${enseigneSlug}`).then(r => setEnseigne(r.data)).catch(() => {}).finally(() => setReady(true));
  }, [enseigneSlug]);

  useEffect(() => {
    if (!enseigneSlug || !enseigne) return;
    const manifestLink = document.querySelector('link[rel="manifest"]');
    if (manifestLink) manifestLink.href = `${BACKEND_URL}/api/v2/manifest/${enseigneSlug}?mode=employee`;
    if (enseigne.logo_url) document.querySelectorAll('link[rel="apple-touch-icon"]').forEach(l => { l.href = `${BACKEND_URL}${enseigne.logo_url}`; });
    document.title = enseigne.pwa_name || enseigne.app_display_name || enseigne.name || 'Logyra RH';
    const metaTheme = document.querySelector('meta[name="theme-color"]');
    if (metaTheme) metaTheme.content = enseigne.primary_color || '#0a1b3d';
    return () => { if (manifestLink) manifestLink.href = '/manifest.json'; document.title = 'Logyra RH'; };
  }, [enseigneSlug, enseigne]);

  const targetFor = (role) => {
    const next = new URLSearchParams(location.search).get('next');
    if (next && next.startsWith('/') && !next.startsWith('//')) return next;
    return role === 'comptable' ? '/comptable' : role === 'employee' ? '/employee/scan' : '/admin/dashboard';
  };

  useEffect(() => {
    if (!authLoading && user && !setup) navigate(targetFor(user.role), { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  const completeLogin = async (data) => {
    if (enseigneSlug) localStorage.setItem('enseigne_slug', enseigneSlug);
    await cacheEnseigne(data.access_token, enseigne);
    if (data.user.needs_password_setup) {
      setChoice(null);
      setSetup({ token: data.access_token, user: data.user });
      return;
    }
    login(data.access_token, data.user);
    toast.success(`Bienvenue ${data.user.first_name} !`);
    navigate(targetFor(data.user.role));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!identifier.trim() || !secret) return toast.error('Saisissez votre identifiant et votre mot de passe');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/unified-login`, { identifier: identifier.trim(), secret });
      if (data.choose_account) {
        setSecret('');
        setChoice(data);
        return;
      }
      await completeLogin(data);
    } catch (error) {
      const status = error.response?.status;
      toast.error(apiErrorText(error, status === 401 ? 'Identifiant ou mot de passe incorrect' : 'Connexion impossible'), { duration: status === 409 ? 8000 : 4000 });
    } finally { setLoading(false); }
  };

  const finishSetup = () => {
    login(setup.token, setup.user);
    toast.success(`Bienvenue ${setup.user.first_name} !`);
    navigate(targetFor(setup.user.role));
  };

  const isPhone = identifier && !identifier.includes('@') && /^[\d\s+.-]*$/.test(identifier);

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0a1b3d 0%, #4578A6 50%, #97BB3A 100%)' }}>
      <div className="absolute top-[-120px] right-[-80px] w-[300px] h-[300px] rounded-full opacity-10" style={{ background: '#E7AF19' }} />
      <div className="absolute bottom-[-80px] left-[-60px] w-[250px] h-[250px] rounded-full opacity-10" style={{ background: '#74BAEB' }} />
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: 'easeOut' }} className="w-full max-w-md relative z-10">
        <OldAppBanner />
        <div className="bg-white rounded-3xl shadow-2xl p-8 sm:p-10" data-testid="unified-login-card">
          <div className="flex justify-center mb-6 h-16">
            {ready && <img src={enseigne?.logo_url ? `${BACKEND_URL}${enseigne.logo_url}` : '/logos/logyra-rh-sans-fond.png'} alt={enseigne?.name || 'Logyra RH'} className="h-16 object-contain" data-testid="login-logo" />}
          </div>
          <h1 className="text-center text-2xl sm:text-3xl font-black tracking-wide mb-1" style={{ color: enseigne?.primary_color || '#0a1b3d' }}>CONNEXION</h1>
          <p className="text-center text-sm text-gray-400 mb-8">Un seul accès pour toute l'équipe : employés, responsables, gérants, comptables</p>

          {setup ? <PasswordSetupStep token={setup.token} onDone={finishSetup} color={color} /> : choice ? (
            <AccountChoiceStep choice={choice} color={color} onCancel={() => setChoice(null)} onPicked={completeLogin} />
          ) : (
            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Email ou numéro de téléphone</label>
                <div className="relative">
                  <AtSign className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input type="text" inputMode={isPhone ? 'tel' : 'email'} autoComplete="username" placeholder="votre@email.fr ou 06 12 34 56 78" value={identifier} onChange={e => setIdentifier(e.target.value)}
                    className="h-12 pl-10 text-base rounded-xl bg-gray-50 border-gray-200" data-testid="login-identifier-input" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Mot de passe ou code PIN</label>
                <div className="relative">
                  <KeyRound className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input type={showSecret ? 'text' : 'password'} autoComplete="current-password" placeholder="Mot de passe ou PIN à 4 chiffres" value={secret} onChange={e => setSecret(e.target.value)}
                    className="h-12 pl-10 pr-12 text-base rounded-xl bg-gray-50 border-gray-200" data-testid="login-secret-input" />
                  <button type="button" onClick={() => setShowSecret(!showSecret)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#4578A6]" data-testid="login-toggle-secret">
                    {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <button type="submit" disabled={loading} className="w-full rounded-xl text-white font-bold text-base uppercase tracking-wider relative overflow-hidden transition-all active:scale-[0.98] disabled:opacity-60"
                style={{ background: 'linear-gradient(180deg, #4578A6 0%, #0a1b3d 100%)', boxShadow: '0 4px 15px rgba(69,120,166,0.4)', padding: '14px 0' }} data-testid="login-submit-button">
                <span className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: '#E7AF19' }} />
                <span className="absolute bottom-0 left-0 right-0 h-[3px]" style={{ background: '#C40050' }} />
                {loading ? <span className="flex items-center justify-center gap-2"><span className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />Connexion...</span> : 'SE CONNECTER'}
              </button>
            </form>
          )}

          {!setup && !choice && (
            <div className="mt-6 text-center space-y-3">
              <button type="button" onClick={() => navigate('/forgot-password')} className="text-sm text-[#4578A6] hover:underline" data-testid="forgot-password-link">Mot de passe ou PIN oublié ?</button>
              <p className="text-xs text-gray-400 leading-relaxed">Première connexion ? Contactez votre responsable pour recevoir vos identifiants.</p>
            </div>
          )}
        </div>
      </motion.div>
      <PWAInstallPrompt />
    </div>
  );
};

export default Login;
